<script lang="ts" module>
	import type { Snippet } from 'svelte';
	import type { ClassValue, SvelteHTMLElements } from 'svelte/elements';
	import type { VariantProps } from 'tailwind-variants';
	import { tv } from '../tv';
	import { CONTAINER_COLOR_VARIANTS, RADII_VARIANTS } from '../variants';

	export const chipVariants = tv({
		base: 'inline-flex cursor-pointer items-center text-(--v-on-container) hover:bg-(--v-container-hover) focus-visible:bg-(--v-container-hover) active:bg-(--v-container-active) aria-pressed:bg-(--v-container-active) aria-selected:bg-(--v-container-active) aria-pressed:hover:bg-(--v-container-hover) aria-selected:hover:bg-(--v-container-hover) aria-pressed:focus-visible:bg-(--v-container-hover) aria-selected:focus-visible:bg-(--v-container-hover) aria-pressed:active:bg-(--v-container-active) aria-selected:active:bg-(--v-container-active) data-[ui-after=true]:pr-(--chip-ui-space) data-[ui-before=true]:pl-(--chip-ui-space) focus-outline disabled-state',
		variants: {
			variant: CONTAINER_COLOR_VARIANTS,
			size: {
				'400': 'h-[1.5rem] gap-s100 px-s300 [--chip-ui-space:var(--spacing-s200)]',
				'500': 'h-[2rem] gap-s200 px-s400 [--chip-ui-space:var(--spacing-s300)]'
			},
			fill: { Soft: 'bg-(--v-container)', None: 'bg-transparent' },
			outlined: { true: 'border-b400 border-solid border-(--v-container-line)' },
			radii: RADII_VARIANTS
		},
		defaultVariants: { variant: 'Surface', fill: 'Soft', size: '400', radii: '400' }
	});

	export type ChipProps<T extends keyof SvelteHTMLElements = 'button'> = Omit<
		SvelteHTMLElements[T],
		'class'
	> &
		VariantProps<typeof chipVariants> & {
			after?: Snippet;
			as?: T;
			before?: Snippet;
			children?: Snippet;
			class?: ClassValue;
			ref?: HTMLElement | null;
		};
</script>

<script lang="ts" generics="T extends keyof SvelteHTMLElements = 'button'">
	let {
		after,
		as = 'button' as T,
		before,
		children,
		class: className,
		fill,
		outlined,
		radii,
		ref = $bindable(null),
		size,
		variant,
		...rest
	}: ChipProps<T> = $props();
</script>

<svelte:element
	this={as}
	bind:this={ref}
	class={[chipVariants({ fill, outlined, radii, size, variant }), className]}
	data-ui-after={after ? true : undefined}
	data-ui-before={before ? true : undefined}
	{...rest}
>
	{@render before?.()}{@render children?.()}{@render after?.()}
</svelte:element>
